import type { RefundEntry } from '@mentora/data-access'
import type { RetryRefundAction } from './retry-refund-button'
import { Badge, Card, CardContent, CardHeader, CardTitle } from '@mentora/ui-components'
import { useFormatter, useTranslations } from 'next-intl'
import { RetryRefundButton } from './retry-refund-button'

// Every refund of a purchase or membership, newest first: how much, who
// asked, whether access went with it, and how far the rail got.

type BadgeVariant = 'success' | 'secondary' | 'destructive'

const STATUS_VARIANT: Record<RefundEntry['status'], BadgeVariant> = {
  issued: 'success',
  requested: 'secondary',
  failed: 'destructive',
}

export function RefundHistory({ refunds, retry }: {
  refunds: RefundEntry[]
  /** Offered on refunds still waiting for the provider; omitted where nothing may act. */
  retry?: RetryRefundAction
}) {
  const t = useTranslations('transactions.refunds')
  const format = useFormatter()
  if (refunds.length === 0)
    return null

  const statusLabel: Record<RefundEntry['status'], string> = {
    issued: t('status.issued'),
    requested: t('status.requested'),
    failed: t('status.failed'),
  }
  const originLabel: Record<RefundEntry['origin'], string> = {
    admin_request: t('origin.admin_request'),
    buyer_request: t('origin.buyer_request'),
    provider_notified: t('origin.provider_notified'),
    backfill: t('origin.backfill'),
  }
  const money = (cents: number, currency: string) => format.number(cents / 100, { style: 'currency', currency: currency.toUpperCase() })

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border/60">
          {refunds.map(r => (
            <li key={r.id} className="flex flex-col gap-1 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-sm tabular-nums text-foreground">{money(r.amountCents, r.currency)}</span>
                <Badge variant={STATUS_VARIANT[r.status]}>{statusLabel[r.status]}</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {format.dateTime(new Date(r.createdAt), { dateStyle: 'medium', timeStyle: 'short' })}
                {' · '}
                {originLabel[r.origin]}
                {r.revokedAccess && ` · ${t('revoked')}`}
              </p>
              {r.reason && <p className="text-sm text-foreground">{r.reason}</p>}
              <p className="font-mono text-xs break-all text-muted-foreground">
                {r.providerRefundId ?? t('noRailId')}
                {r.settlementFeeCents !== null && r.settlementCurrency && ` · ${t('fee', { fee: money(r.settlementFeeCents, r.settlementCurrency) })}`}
              </p>
              {r.status === 'requested' && (
                <>
                  <p className="text-xs text-muted-foreground">{t('awaitingHint')}</p>
                  {retry && <RetryRefundButton refundId={r.id} retry={retry} />}
                </>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
